import React, { useEffect, useState } from "react"
import {motion} from 'framer-motion'
import { useFormik } from "formik"
import * as Yup from 'yup'
import FieldInput from "../../Components/FieldInput"
import API from "../../Common/API"
import Button from "../../Components/Button"
import { useDispatch } from "react-redux"
import { setAlert } from "../../Store/Theme/actions"

const initialPassword = {
    mobileNumber :'',
    confirmPassword:'',
    password : ''
}

const normalizePhoneDigits = (value) => (value || "").toString().replace(/\D/g, "")
const REQUEST_TIMEOUT_MS = 12000

const ChangePassword = ({
    embedded = false,
    onClose,
    defaultMobile = "",
    defaultUserId = "",
    defaultUserName = "",
    onChanged,
    onCloseLockChange,
})=>{


    const [userDetails,setUserDetails] = useState()
    const [savedCredentials, setSavedCredentials] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const dispatch =  useDispatch()

    const passwordSchema = Yup.object().shape({
        mobileNumber : Yup.string().required("Mobile number is required"),
        confirmPassword : Yup.string().required("Confirm Password is required"),
        password : Yup.string().required("Password is required")
    })

    const passwordForm = useFormik({
        initialValues:{ ...initialPassword, mobileNumber: defaultMobile || '' },
        validateOnMount:true,
        validationSchema:passwordSchema,
        enableReinitialize : true,
        onSubmit:(values)=>{
            const normalizedMobile = normalizePhoneDigits(values.mobileNumber)
            const resolvedUserId = userDetails?._id || defaultUserId
            const payloadAttempts = [
                {
                    mobileNumber: normalizedMobile,
                    userName: normalizedMobile,
                    userId: resolvedUserId,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                    newPassword: values.password,
                    confirmNewPassword: values.confirmPassword,
                    contactInformation: {
                        mobileNumber: normalizedMobile,
                    },
                    additionalInformation: {
                        password: values.password,
                        confirmPassword: values.confirmPassword,
                    },
                },
                {
                    userId: resolvedUserId,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                },
                {
                    id: resolvedUserId,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                },
                {
                    mobileNumber: normalizedMobile,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                },
                {
                    mobileNumber: normalizedMobile,
                    newPassword: values.password,
                    confirmNewPassword: values.confirmPassword,
                },
                {
                    userName: normalizedMobile,
                    password: values.password,
                    confirmPassword: values.confirmPassword,
                },
            ]

            if(values.password === values.confirmPassword){
                ;(async () => {
                    setSubmitting(true)

                    let lastErrorMessage = 'Unable to update password'

                    try {
                        for (const payload of payloadAttempts) {
                            try {
                                const changeRes = await API.post(`/auth/changePasswordForUser`, payload, {
                                    timeout: REQUEST_TIMEOUT_MS,
                                })
                                const backendResetError =
                                    changeRes?.data?.error ||
                                    changeRes?.data?.message === 'error' ||
                                    changeRes?.data?.data?.error

                                if (backendResetError) {
                                    lastErrorMessage =
                                        changeRes?.data?.data?.message ||
                                        changeRes?.data?.message ||
                                        'Unable to update password'
                                    continue
                                }

                                if (changeRes.status === 200) {
                                    setSavedCredentials({
                                        mobileNumber: normalizedMobile,
                                        password: values.password,
                                    })
                                    passwordForm.resetForm({
                                        values: {
                                            mobileNumber: normalizedMobile,
                                            password: '',
                                            confirmPassword: '',
                                        }
                                    })
                                    dispatch(setAlert({title:'Success',subtitle:'Password updated successfully.',type:'success',active:true}))
                                    if (typeof onChanged === 'function') onChanged(changeRes.data?.data)
                                    return
                                }
                            } catch (error) {
                                lastErrorMessage =
                                    error?.code === 'ECONNABORTED'
                                        ? 'Password reset request timed out. Please try again.'
                                        : error?.response?.data?.error || error?.response?.data?.message || 'Unable to update password'
                            }
                        }

                        dispatch(setAlert({title:'Error',subtitle:lastErrorMessage,type:'error',active:true}))
                    } finally {
                        setSubmitting(false)
                    }
                })()
            }else{
                dispatch(setAlert({title:'Error',subtitle:"Password and confirm password should be same",type:'error',active:true}))
            }
        }
    })

    const getUserDetails = (mobileNumber = passwordForm.values.mobileNumber)=>{
        if (!mobileNumber) return
        API.get(`/user/getUserDetailsByMobile/${mobileNumber}`).then((apiRes)=>{
                if(apiRes.status === 200){
                    setUserDetails(apiRes.data.data)
                }
            }).catch((err)=>{
                dispatch(setAlert({title:'Error',subtitle:'Account not found',type:'error',active:true}))
            })
    }

    useEffect(() => {
        if (defaultMobile) {
            passwordForm.setFieldValue('mobileNumber', defaultMobile)
            getUserDetails(defaultMobile)
        }
    }, [defaultMobile]) // eslint-disable-line

    useEffect(() => {
        if (typeof onCloseLockChange === "function") {
            onCloseLockChange(false)
        }
    }, [onCloseLockChange])

    const resetAndClose = () => {
        passwordForm.resetForm()
        setUserDetails(undefined)
        setSavedCredentials(null)
        if (typeof onClose === 'function') onClose()
    }

    const copyCredentials = async () => {
        if (!savedCredentials?.mobileNumber || !savedCredentials?.password) return

        const credentialText = `Mobile: ${savedCredentials.mobileNumber}\nPassword: ${savedCredentials.password}`

        try {
            await navigator.clipboard.writeText(credentialText)
            dispatch(setAlert({
                title: 'Copied',
                subtitle: 'Member credentials copied successfully.',
                type: 'success',
                active: true,
            }))
        } catch {
            dispatch(setAlert({
                title: 'Error',
                subtitle: 'Unable to copy credentials. Please try again.',
                type: 'error',
                active: true,
            }))
        }
    }


    return (
        <motion.div className={`grid grid-cols-1 gap-5 ${embedded ? '' : 'px-5'}`}>
            <div className="grid gap-2">
                <p className="text-sm font-bold uppercase tracking-[0.18em] text-primeColor">Account Security</p>
                <p className="text-sm leading-7 text-tColor">Search the member, confirm the profile, and update the password from the same workspace.</p>
            </div>
            <motion.div className="grid gap-4 rounded-2xl border border-borderColor bg-white p-5 shadow-sm md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                <FieldInput label={'Mobile number'} type="phone" fieldProps={passwordForm.getFieldProps('mobileNumber')} fieldMeta={passwordForm.getFieldMeta('mobileNumber')} fieldHelper={passwordForm.getFieldHelpers('mobileNumber')} edit={true} dataLoaded={true}/>
                <Button type={'primary'} injectClass="px-6" onClick={()=>getUserDetails()}>Search User</Button>
            </motion.div>
            {
                (userDetails || defaultUserId) && <motion.div className="grid gap-4 rounded-2xl border border-borderColor bg-lightGrey/35 p-5 shadow-sm md:grid-cols-2">
                    <motion.div className="grid gap-1">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">Name</p>
                        <p className="text-base font-semibold text-tColor">{userDetails?.firstName || defaultUserName || '-'}</p>
                    </motion.div>
                    <motion.div className="grid gap-1">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-tpColor">Ref Id</p>
                        <p className="text-base font-semibold text-tColor">{userDetails?.refId || '-'}</p>
                    </motion.div>
                    </motion.div>
            }
            <motion.div className="grid max-w-xl grid-cols-1 gap-4 rounded-2xl border border-borderColor bg-white p-5 shadow-sm">
                <FieldInput label={'Password'} type="password" fieldProps={passwordForm.getFieldProps('password')} fieldMeta={passwordForm.getFieldMeta('password')} fieldHelper={passwordForm.getFieldHelpers('password')} edit={true} dataLoaded={true}/>
                <FieldInput label={'Confirm Password'} type="password" fieldProps={passwordForm.getFieldProps('confirmPassword')} fieldMeta={passwordForm.getFieldMeta('confirmPassword')} fieldHelper={passwordForm.getFieldHelpers('confirmPassword')} edit={true} dataLoaded={true}/>
                {savedCredentials && (
                    <div className="rounded-2xl border border-borderColor bg-slate-50 p-4">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-primeColor">Saved credentials</p>
                        <p className="mt-2 text-sm font-semibold text-tColor">Mobile: {savedCredentials.mobileNumber}</p>
                        <p className="mt-1 text-sm font-semibold text-tColor">Password: {savedCredentials.password}</p>
                        <p className="mt-2 inline-flex rounded-full bg-[#ecf8ef] px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-[#25633a]">Updated</p>
                        <p className="mt-3 text-sm leading-6 text-tpColor">
                            Backend confirmed the password update. You can now share these credentials with the member.
                        </p>
                        <div className="mt-4">
                            <Button type="secondary" onClick={copyCredentials}>Copy Credentials</Button>
                        </div>
                    </div>
                )}
                <div className="flex flex-wrap gap-3 pt-2">
                    {embedded && <Button type={'secondary'} onClick={resetAndClose}>Cancel</Button>}
                    <Button  type={'primary'} onClick={()=>passwordForm.submitForm()} disabled={submitting}>{submitting ? 'Updating...' : 'Update Password'}</Button>
                </div>
            </motion.div>
        </motion.div>
    )
}

export default ChangePassword
